import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/** Formate une date ISO en JJ.MM.AAAA (esthétique flyer). */
function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export default function Home() {
  const { isAuthenticated } = useAuth();
  const [artists, setArtists] = useState([]);
  const [events, setEvents] = useState([]);

  // CTA d'inscription : vers le dashboard si déjà connecté
  const signupTo = isAuthenticated ? '/dashboard' : '/register';

  useEffect(() => {
    async function fetchData() {
      const [artistsRes, eventsRes] = await Promise.allSettled([
        api.get('/artists?limit=6'),
        api.get('/events?limit=6'),
      ]);
      if (artistsRes.status === 'fulfilled') {
        setArtists(artistsRes.value.data.artists || []);
      }
      if (eventsRes.status === 'fulfilled') {
        setEvents(eventsRes.value.data.events || []);
      }
    }
    fetchData();
  }, []);

  const tickerEvents = events.slice(0, 8);
  const featuredEvents = events.slice(0, 3);

  return (
    <div className="animate-fadeIn">
      {/* ── Hero plein écran ── */}
      <section className="relative th-bg border-b th-border overflow-hidden">
        <span className="hidden sm:block absolute top-7 right-6 lg:right-8 th-kicker">
          Plateforme de booking · depuis 2025
        </span>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[560px] sm:min-h-[78vh] flex flex-col justify-center py-20">
          <div className="th-kicker th-kicker-accent mb-5 animate-fadeInUp">
            Techno · Indus · Ambient · Electro
          </div>
          <h1 className="th-display-xl max-w-[14ch] animate-fadeInUp" style={{ animationDelay: '0.05s' }}>
            La scène <em>ne dort</em> jamais.
          </h1>
          <p
            className="th-mono-meta mt-7 max-w-[52ch] leading-loose animate-fadeInUp"
            style={{ animationDelay: '0.1s' }}
          >
            Striderr relie les artistes underground, les promoteurs et les bookers.
            Sans intermédiaire superflu, sans paillettes.
          </p>
          <div
            className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fadeInUp"
            style={{ animationDelay: '0.15s' }}
          >
            <Link to="/artists" className="th-btn-accent text-center">
              Explorer les artistes
            </Link>
            <Link to={signupTo} className="th-btn text-center">
              Publier un événement
            </Link>
          </div>
        </div>
      </section>

      {/* ── Ticker événements ── */}
      {tickerEvents.length > 0 && (
        <div className="th-ticker" aria-label="Événements à venir">
          <div className="th-ticker-track py-3" aria-hidden="true">
            {[0, 1].map((dup) =>
              tickerEvents.map((ev) => (
                <span key={`${dup}-${ev.id}`}>
                  <b>{ev.title}</b>
                  {ev.venueName ? ` · ${ev.venueName}` : ''}
                  {ev.location?.city ? `, ${ev.location.city}` : ''} · {formatEventDate(ev.eventDate)}
                  <span className="th-ticker-sep">+++</span>
                </span>
              ))
            )}
          </div>
          {/* Équivalent statique pour les lecteurs d'écran */}
          <ul className="sr-only">
            {tickerEvents.map((ev) => (
              <li key={ev.id}>
                {ev.title} · {ev.location?.city} · {formatEventDate(ev.eventDate)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Artistes en vedette ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="th-section-head mb-7">
          <div>
            <div className="th-kicker mb-2.5">Annuaire / sélection</div>
            <h2 className="th-display-lg">Artistes en vedette</h2>
          </div>
          <Link to="/artists" className="th-link-mono">
            Tout l'annuaire →
          </Link>
        </div>

        {artists.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px th-bg-surface border th-border">
            {artists.map((artist, i) => (
              <Link
                key={artist.id}
                to={`/artists/${artist.slug}`}
                className="group th-bg no-underline animate-fadeInUp"
                style={{ animationDelay: `${0.04 * i}s` }}
              >
                {artist.profilePhotoUrl ? (
                  <img
                    src={artist.profilePhotoUrl}
                    alt={artist.artistName}
                    className="w-full aspect-square th-photo-duotone"
                  />
                ) : (
                  <div className="w-full aspect-square th-bg-elevated flex items-center justify-center font-serif text-5xl th-text-muted group-hover:text-accent transition-colors duration-150">
                    {artist.artistName?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <div className="p-4">
                  <div className="font-mono text-[13px] font-bold uppercase tracking-[0.1em] th-text group-hover:text-accent transition-colors duration-150">
                    {artist.artistName}
                  </div>
                  {artist.genres && artist.genres.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {artist.genres.slice(0, 2).map((genre) => (
                        <span key={genre.id} className="th-badge th-border">
                          {genre.name}
                        </span>
                      ))}
                    </div>
                  )}
                  {artist.location?.city && (
                    <div className="th-mono-meta mt-2 normal-case">
                      {artist.location.city}
                      {artist.location.country ? ` · ${artist.location.country}` : ''}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="border th-border th-bg-surface p-10 text-center th-mono-meta">
            L'annuaire se remplit. Reviens bientôt.
          </div>
        )}
      </section>

      {/* ── Événements publiés (tracklist) ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
        <div className="th-section-head mb-7">
          <div>
            <div className="th-kicker mb-2.5">Prochaines dates</div>
            <h2 className="th-display-lg">Événements publiés</h2>
          </div>
        </div>

        {featuredEvents.length > 0 ? (
          <div className="border-t th-border">
            {featuredEvents.map((ev, i) => (
              <Link
                key={ev.id}
                to={`/events/${ev.slug}`}
                className="th-event-row flex-wrap sm:flex-nowrap"
              >
                <span className="th-event-row-num">{String(i + 1).padStart(2, '0')}.</span>
                <span className="th-display-md th-event-row-title flex-1 transition-colors duration-150">
                  {ev.title}
                </span>
                <span className="th-mono-meta normal-case">
                  {ev.venueName}
                  {ev.location?.city ? ` · ${ev.location.city}` : ''}
                </span>
                <span className="th-mono-meta th-text">{formatEventDate(ev.eventDate)}</span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="border th-border th-bg-surface p-10 text-center th-mono-meta">
            Aucun événement publié pour l'instant.
          </div>
        )}
      </section>

      {/* ── Recrutement par rôle ── */}
      <section className="border-t th-border">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3">
          {[
            {
              n: '01',
              role: 'Artiste',
              verb: 'Soyez bookable.',
              desc: 'Profil public, calendrier de disponibilités, bookings reçus en direct.',
              cta: 'Créer mon profil',
            },
            {
              n: '02',
              role: 'Promoteur',
              verb: 'Bookez juste.',
              desc: 'Recherche par genre, ville et disponibilité réelle. Invitation en deux clics.',
              cta: 'Publier un événement',
            },
            {
              n: '03',
              role: 'Booker',
              verb: 'Gérez le roster.',
              desc: 'Suivez les bookings de vos artistes, centralisez les échanges.',
              cta: 'Ouvrir une agence',
            },
          ].map((r, i) => (
            <div
              key={r.n}
              className={`flex flex-col items-start gap-3.5 p-8 sm:p-10 th-border ${
                i < 2 ? 'border-b md:border-b-0 md:border-r' : ''
              }`}
            >
              <div className="th-kicker th-kicker-accent">
                {r.n} · {r.role}
              </div>
              <div className="th-display-md">{r.verb}</div>
              <p className="th-mono-meta normal-case leading-relaxed">{r.desc}</p>
              <Link
                to={signupTo}
                className="th-btn mt-auto text-center text-[10px] px-4 py-2"
              >
                {r.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
